import { useAuth } from "@/core/components/auth/store/auth";
import { MemberFilter } from "@/dashboard/components/dashboard-filters/components/member-filter";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { Badge, Button } from "@woba/ds-web-next";
import { useState } from "react";
import { DashboardFiltersSheet } from "../dashboard-filters-sheet";
import { DateQuickFilters } from "./components/date-filters/components/date-quick-filters";
import { MoreFiltersIcon } from "./components/more-filters-icon";
import { SpaceTypeFilter } from "./components/space-type-filter";
import { useTranslation } from "react-i18next";

interface DashboardFiltersProps {
  withDefaultFilters?: boolean;
  filterButtonLabel?: string;
}

/**
 * Filtros do Painel (Figma 6037:4452): Data em Chips na primeira linha;
 * Colaboradores, Tipos de espaço e "Mais filtros" na segunda. No mobile tudo
 * empilha e "Mais filtros" ocupa a largura toda.
 */
export function DashboardFilters({
  withDefaultFilters = true,
  filterButtonLabel,
}: DashboardFiltersProps) {
  const [translate] = useTranslation(["dashboard"]);
  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false);
  const { filterCount } = useDashboardFilters();
  const {
    userInfo: { user },
  } = useAuth();

  const moreFiltersLabel =
    filterButtonLabel ??
    (translate("dashboard-filters-button-label-default") as string);

  return (
    <div
      className="flex flex-col gap-4 min-w-0"
      data-testid="dashboard-filters"
    >
      {withDefaultFilters && (
        <>
          <DateQuickFilters
            appearance="chips"
            event="DASHBOARD_USE_FILTER_DATE"
            eventCategory="DASHBOARD"
          />

          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            {user && <MemberFilter location="dashboard" />}

            <SpaceTypeFilter location="dashboard" />

            <div className="shrink-0">
              {/* Figma: 40px (medium) no mobile e 48px (large) no desktop. */}
              {(["medium", "large"] as const).map((size) => (
                <div
                  key={size}
                  className={
                    size === "medium"
                      ? "md:hidden [&>button]:w-full"
                      : "hidden md:block"
                  }
                >
                  <Button
                    appearance="secondary"
                    size={size}
                    leading
                    leadingIcon={({ color, size: iconSize }) => (
                      <MoreFiltersIcon color={color} size={iconSize} />
                    )}
                    accessibilityLabel={moreFiltersLabel}
                    onPress={() => setIsFiltersSheetOpen(true)}
                  >
                    <span className="inline-flex gap-2 items-center">
                      {moreFiltersLabel}
                      {filterCount > 0 && (
                        <Badge variant="neutral" label={String(filterCount)} />
                      )}
                    </span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <DashboardFiltersSheet
        open={isFiltersSheetOpen}
        onOpenChange={setIsFiltersSheetOpen}
      />
    </div>
  );
}
